import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Spinner } from 'react-bootstrap';
import { UpdatePasswordRequest } from '../../_core/_request.ts'; // Adjust path
import { AuthContext } from '../../context/AuthContext'; // Adjust path
import PasswordInput from '../Tools/PasswordInput.tsx';

const ChangePassword = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Assuming your AuthContext exposes the logged-in user's ID
  const { user, logout } = useContext(AuthContext);

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  // Optional: check if they were routed here due to a forced reset
  const isForcedReset = location.state?.forcedReset || false;

  useEffect(() => {
    if (isForcedReset) {
      setMessage('For your security, you must change your password before continuing.');
    }
  }, [isForcedReset]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setStatus('error');
      setMessage('New passwords do not match.');
      return;
    }

    if (newPassword.length < 8) {
      setStatus('error');
      setMessage('New password must be at least 8 characters long.');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      // Ensure we have a userId to pass to the backend
      const userId = user?.userID || sessionStorage.getItem('userID');

      if (!userId) {
        throw new Error("User ID not found. Please log in again.");
      }

      await UpdatePasswordRequest({
        UserId: parseInt(userId, 10),
        OldPassword: oldPassword,
        NewPassword: newPassword
      });

      setStatus('success');
      setMessage('Password updated successfully!');

      // If it was a forced reset, redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (err: any) {
      setStatus('error');
      setMessage(err.response?.data || err.message || 'Failed to update password. Please check your old password.');
    }
  };

  const variants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 }
  };

  return (
    <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
      <motion.div
        className="card shadow p-4 w-100"
        style={{ maxWidth: '500px' }}
        initial="initial"
        animate="animate"
        variants={variants}
      >
        <div className="card-body d-flex flex-column gap-3">
          <h2 className="card-title text-center text-primary mb-2">Change Password</h2>

          {message && status !== 'success' && (
            <div className={`alert ${status === 'error' ? 'alert-danger' : 'alert-info'} small`}>
              {message}
            </div>
          )}

          {status === 'success' ? (
            <div className="text-center">
              <div className="alert alert-success">{message}</div>
              <p className="text-muted">Redirecting...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
              <div>
                <label className="form-label">Current Password</label>
                <PasswordInput name="password"
                  readOnly={false}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)} />
              </div>

              <div>
                <label className="form-label">New Password</label>
                <PasswordInput name="password"
                  readOnly={false}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)} />
              </div>

              <div>
                <label className="form-label">Confirm New Password</label>
                <PasswordInput name="confirmPassword"
                  readOnly={false}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)} />
              </div>

              <div className="d-flex gap-2 mt-3">
                <button className="btn btn-primary flex-grow-1" type="submit" disabled={status === 'loading' || !oldPassword || !newPassword || !confirmPassword}>
                  {status === 'loading' ? (
                    <span><Spinner animation="border" size="sm" className="me-2" />Updating...</span>
                  ) : (
                    'Update Password'
                  )}
                </button>

                {/* Only show cancel/logout button if not forced, or let them logout to escape the forced reset */}
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => isForcedReset ? logout() : navigate(-1)}
                  disabled={status === 'loading'}
                >
                  {isForcedReset ? 'Cancel & Logout' : 'Cancel'}
                </button>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ChangePassword;